import { FC, KeyboardEvent, useState } from 'react';
import { Input } from 'reactstrap';
import ActiveFilterChips from './ActiveFilterChips';
import { MAX_RADIUS_MI } from './context';
import { FiltersRailProps } from './types';

/**
 * Sticky 262px filters rail (README §Filters rail; STORY-2A-SPEC rule 4).
 * Minimum experience 0-20yrs, skills (all-required, free-text add/remove chips — the
 * API has no skills-taxonomy lookup to populate a real <select> from), and a
 * location radius (10-250mi, "Any distance" sentinel at the max).
 *
 * The location box drives the radius center (kan-83): typing updates the label
 * and flips `geoCenterCustom` (so context.tsx stops auto-recentering from the
 * job), while BLUR or ENTER commits the text through `onCenterCommit`, which
 * resolves it to coordinates via the ATS location service. Committing per
 * keystroke would spend a geocode per character. `geoResolveStatus` reports
 * that resolution in the message slot under the slider; `radiusDisabled` (still
 * resolving, or no coordinates) greys out just the radius slider, per the lane
 * contract — it must never block the rest of the rail. Moving the radius slider
 * flips `radiusCustom`, so the recruiter's radius survives re-selecting the same
 * job (kan-78 item 1 defaults it to 20mi around the job's location).
 */

const MAX_SKILLS = 50;

const RAIL_STYLE: React.CSSProperties = {
  position: 'sticky',
  top: 16,
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
};

const SECTION_STYLE: React.CSSProperties = { marginBottom: 20 };
const LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#667085',
  display: 'block',
  marginBottom: 6,
};
const VALUE_STYLE: React.CSSProperties = { fontSize: 12, color: '#1F2937', fontWeight: 600 };

const CLEAR_LINK_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#E08A14',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
};

const SKILL_CHIP_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#F6F7F9',
  border: '1px solid #E2E5EA',
  color: '#1F2937',
  fontSize: 12,
  fontWeight: 500,
  padding: '3px 8px',
  borderRadius: 999,
  marginRight: 6,
  marginBottom: 6,
};

const CHIP_REMOVE_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#667085',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
};

const ADD_BTN_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #D0D5DD',
  color: '#1F2937',
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  padding: '0 12px',
  cursor: 'pointer',
};

const HINT_STYLE: React.CSSProperties = { fontSize: 11, color: '#98A2B3', marginTop: 6 };

const FiltersRail: FC<FiltersRailProps> = ({
  filters,
  onChange,
  onCenterCommit,
  onClearAll,
  radiusDisabled,
  geoResolveStatus,
  geoFormattedAddress,
}) => {
  const [skillDraft, setSkillDraft] = useState('');

  const addSkill = (): void => {
    const value = skillDraft.trim();
    if (!value || filters.skills.length >= MAX_SKILLS) return;
    if (filters.skills.some((s) => s.toLowerCase() === value.toLowerCase())) {
      setSkillDraft('');
      return;
    }
    onChange({ skills: [...filters.skills, value] });
    setSkillDraft('');
  };

  const removeSkill = (skill: string): void => {
    onChange({ skills: filters.skills.filter((s) => s !== skill) });
  };

  const handleSkillKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const handleCenterKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onCenterCommit(filters.geoCenterLabel);
    }
  };

  const skillsAtMax = filters.skills.length >= MAX_SKILLS;
  const radiusLabel = filters.radiusMi >= MAX_RADIUS_MI ? 'Any distance' : `${filters.radiusMi} mi`;

  return (
    <div style={RAIL_STYLE}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>Filters</span>
        <button type="button" onClick={onClearAll} style={CLEAR_LINK_STYLE}>
          Clear all
        </button>
      </div>

      <div style={SECTION_STYLE}>
        <label style={LABEL_STYLE}>
          Minimum experience <span style={VALUE_STYLE}>{filters.minYears} yrs</span>
        </label>
        <input
          type="range"
          min={0}
          max={20}
          step={1}
          value={filters.minYears}
          onChange={(e) => onChange({ minYears: Number(e.target.value) })}
          style={{ width: '100%' }}
          aria-label="Minimum years experience"
        />
      </div>

      <div style={SECTION_STYLE}>
        <label style={LABEL_STYLE}>
          Skills ({filters.skills.length}/{MAX_SKILLS}) — all required
        </label>
        {filters.skills.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            {filters.skills.map((skill) => (
              <span key={skill} style={SKILL_CHIP_STYLE}>
                {skill}
                <button
                  type="button"
                  style={CHIP_REMOVE_STYLE}
                  onClick={() => removeSkill(skill)}
                  aria-label={`Remove skill ${skill}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <Input
            type="text"
            value={skillDraft}
            placeholder={skillsAtMax ? 'Max 50 skills' : 'Add a required skill…'}
            disabled={skillsAtMax}
            onChange={(e) => setSkillDraft(e.target.value)}
            onKeyDown={handleSkillKeyDown}
            style={{ height: 32, fontSize: 13 }}
          />
          <button
            type="button"
            onClick={addSkill}
            disabled={!skillDraft.trim() || skillsAtMax}
            style={{ ...ADD_BTN_STYLE, opacity: !skillDraft.trim() || skillsAtMax ? 0.5 : 1 }}
          >
            Add
          </button>
        </div>
      </div>

      <div style={{ ...SECTION_STYLE, marginBottom: 0, opacity: radiusDisabled ? 0.55 : 1 }}>
        <label style={LABEL_STYLE}>Location radius</label>
        <Input
          type="text"
          value={filters.geoCenterLabel}
          placeholder="City, ST"
          onChange={(e) => onChange({ geoCenterLabel: e.target.value, geoCenterCustom: true })}
          onBlur={() => onCenterCommit(filters.geoCenterLabel)}
          onKeyDown={handleCenterKeyDown}
          style={{ height: 32, fontSize: 13, marginBottom: 10 }}
        />
        <label style={LABEL_STYLE}>
          Within <span style={VALUE_STYLE}>{radiusLabel}</span>
        </label>
        <input
          type="range"
          min={10}
          max={MAX_RADIUS_MI}
          step={10}
          value={filters.radiusMi}
          disabled={radiusDisabled}
          onChange={(e) => onChange({ radiusMi: Number(e.target.value), radiusCustom: true })}
          style={{ width: '100%' }}
          aria-label="Radius in miles"
        />
        {geoResolveStatus === 'resolving' && <div style={HINT_STYLE}>Resolving location…</div>}
        {geoResolveStatus === 'noMatch' && (
          <div style={HINT_STYLE}>
            Couldn't find that location — check the spelling to enable the radius filter.
          </div>
        )}
        {geoResolveStatus === 'error' && (
          <div style={HINT_STYLE}>
            Couldn't resolve that location right now — try again to enable the radius filter.
          </div>
        )}
        {geoResolveStatus === 'resolved' && geoFormattedAddress && (
          <div style={HINT_STYLE}>Centered on {geoFormattedAddress}</div>
        )}
      </div>

      <ActiveFilterChips filters={filters} onChange={onChange} />
    </div>
  );
};

export default FiltersRail;
